import { ReactComponent as CloseLogo } from '@percent/cause-dashboard/common/assets/images/close.svg'
import styles from './PopUpModalContainer.module.scss'
import {
  POSTHOG_FEATURE_FLAGS,
  useCausesPortalAnalytics,
  useFeatureFlagsReady,
  useGfmBannerVariant,
  useIntroModalState,
  usePostHogFeatureFlag
} from '../../hooks'
import { useEffect, useState } from 'react'
import { Modal } from '@percent/lemonade'
import { InterestModal } from './interestModal/InterestModal'
import { SmartApplyVariantA } from './smartApplyVariantA/SmartApplyVariantA'
import { SmartApplyPartnerLogos } from './smartApplyPartnerLogos/SmartApplyPartnerLogos'
import { SmartApplyRouter } from './SmartApplyRouter'
import type { SmartApplyPartner } from '@percent/cause-dashboard/constants/smartApplyPartnerConfig'
import { GrantAdvisoryBookingModal } from '@percent/cause-dashboard/common/components/grantAdvisoryBookingModal'
import {
  GRANT_ADVISORY_BOOKING_TRACKING,
  SMART_APPLY_GRANT_ADVISORY_EVENTS
} from '@percent/cause-dashboard/common/components/grantAdvisoryBookingModal/grantAdvisoryBooking.utils'
import { useGrantAdvisoryTrackingProperties } from '@percent/cause-dashboard/common/components/grantAdvisoryBookingModal/useGrantAdvisoryTrackingProperties'
import { useGrantAdvisoryBookingUrl } from '@percent/cause-dashboard/common/components/grantAdvisoryBookingModal/useGrantAdvisoryBookingUrl'

type ModalContainerProps = {
  hasCanvaApplication: boolean
}

export function PopUpModalContainer({ hasCanvaApplication }: Readonly<ModalContainerProps>) {
  const { shouldShowSmartApply, recordSmartApplyAction, shouldShowGfmInterestModal } = useIntroModalState()
  const { track } = useCausesPortalAnalytics()
  const areFlagsReady = useFeatureFlagsReady()
  const { isReady: isGfmModalReady, variant: gfmModalVariant } = useGfmBannerVariant('modal')
  const grantAdvisoryTrackingProperties = useGrantAdvisoryTrackingProperties()
  const grantAdvisoryBookingUrl = useGrantAdvisoryBookingUrl('smartApply', GRANT_ADVISORY_BOOKING_TRACKING.smartApply)
  const areDynamicLogosEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CP_SMART_APPLY_DYNAMIC_LOGOS)
  const isGrantAdvisoryAfterSmartApplyEnabled = usePostHogFeatureFlag(
    POSTHOG_FEATURE_FLAGS.CP_GRANT_ADVISORY_AFTER_SMART_APPLY
  )

  const [isSmartApplyPopupOpen, setIsSmartApplyPopupOpen] = useState(shouldShowSmartApply)
  const [isGrantAdvisoryOpen, setIsGrantAdvisoryOpen] = useState(false)
  const [interestModalDone, setInterestModalDone] = useState(false)
  const [modalWidth, setModalWidth] = useState(window.innerWidth > 1144 ? 1134 : window.innerWidth * 0.95)
  const [modalHeight, setModalHeight] = useState(window.innerHeight > 750 ? 740 : window.innerHeight * 0.95)
  const [selectedPartner, setSelectedPartner] = useState<SmartApplyPartner | null>(null)
  const [appliedPartners, setAppliedPartners] = useState<Set<SmartApplyPartner>>(new Set())

  useEffect(() => {
    setIsSmartApplyPopupOpen(shouldShowSmartApply)
  }, [shouldShowSmartApply])

  const handleClose = () => {
    setIsSmartApplyPopupOpen(false)
    recordSmartApplyAction('dismissed')

    if (isGrantAdvisoryAfterSmartApplyEnabled && grantAdvisoryBookingUrl) {
      track(SMART_APPLY_GRANT_ADVISORY_EVENTS.shown, grantAdvisoryTrackingProperties)
      setIsGrantAdvisoryOpen(true)
    }
  }

  const handleApplicationSuccess = () => {
    if (selectedPartner) {
      setAppliedPartners(prev => new Set(prev).add(selectedPartner))
      setSelectedPartner(null)
    }
  }

  useEffect(() => {
    const handleResize = () => {
      setModalWidth(window.innerWidth > 1144 ? 1134 : window.innerWidth * 0.95)
      setModalHeight(window.innerHeight > 750 ? 740 : window.innerHeight * 0.95)
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  if (isGrantAdvisoryOpen && grantAdvisoryBookingUrl) {
    return (
      <GrantAdvisoryBookingModal
        onClose={() => setIsGrantAdvisoryOpen(false)}
        entryPoint="smartApply"
        bookingUrl={grantAdvisoryBookingUrl}
        onScheduled={() => track(SMART_APPLY_GRANT_ADVISORY_EVENTS.scheduled, grantAdvisoryTrackingProperties)}
        analyticsProperties={grantAdvisoryTrackingProperties}
      />
    )
  }

  /*
    Above the Smart Apply guards on purpose: this route and /home must agree on who is in the
    test, or the page a user lands on first decides whether they are counted. Only users who
    could still see it wait on the flag.
  */
  const isGfmModalPending = shouldShowGfmInterestModal && !interestModalDone

  if (isGfmModalPending && !isGfmModalReady) {
    return null
  }

  // `!selectedPartner`: never pull a user out of an application in progress
  if (isGfmModalPending && gfmModalVariant && !selectedPartner) {
    return (
      <InterestModal
        variant={gfmModalVariant}
        width={modalWidth}
        height={modalHeight}
        onDismiss={() => setInterestModalDone(true)}
      />
    )
  }

  if (!isSmartApplyPopupOpen) {
    return null
  }

  if (!areFlagsReady) {
    return null
  }

  if (selectedPartner) {
    return (
      <SmartApplyRouter
        selectedPartner={selectedPartner}
        onClose={() => setSelectedPartner(null)}
        onSuccess={handleApplicationSuccess}
      />
    )
  }

  return (
    <div id="pop-up-modal-container">
      <div className={styles.backgroundBlur}></div>
      <Modal
        open={isSmartApplyPopupOpen}
        data-testid="pop-up-modal-container"
        width={modalWidth}
        height={modalHeight}
        overflowHidden
        onClose={handleClose}
      >
        <div className={styles.modalCtn} style={{ height: modalHeight, width: '100%', maxWidth: modalWidth }}>
          <div className={`${styles.column} ${styles.leftColumn}`}>
            <button
              title="Close modal"
              id={styles.btnCloseDialog}
              onClick={handleClose}
              type="button"
              data-testid="close-button"
            >
              <CloseLogo />
            </button>
            <div className={`${styles.leftCtn} ${styles.lessPadding}`}>
              <SmartApplyVariantA
                hasCanvaApplication={hasCanvaApplication}
                setSelectedPartner={setSelectedPartner}
                appliedPartners={appliedPartners}
                onClose={handleClose}
              />
            </div>
          </div>
          {areDynamicLogosEnabled ? (
            <div className={`${styles.column} ${styles.rightVariantColumn}`}>
              <SmartApplyPartnerLogos />
            </div>
          ) : (
            <div className={`${styles.column} ${styles.rightColumn}`} />
          )}
        </div>
      </Modal>
    </div>
  )
}
